import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faGear } from '@fortawesome/pro-light-svg-icons/faGear';
import { faTowerBroadcast } from '@fortawesome/pro-light-svg-icons/faTowerBroadcast';
import { faCreditCard } from '@fortawesome/pro-light-svg-icons/faCreditCard';
import { faLifeRing } from '@fortawesome/pro-light-svg-icons/faLifeRing';
import { faStar } from '@fortawesome/pro-light-svg-icons/faStar';
import { SMLRouteParts, SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../settings/tabs/types';
import { settingsTabTarget } from '../../../settings/tabs/settingsTabTarget';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { smlNavigationChromeMessages as chrome, smlNavigationMessages } from '../smlNavigationMessages';
import { accountMenuMessages as messages } from './accountMenuMessages';
import { reviewMessages } from '../../review/reviewMessages';

export interface AccountMenuLink {
  key: 'settings' | 'connections' | 'plans' | 'help' | 'review';
  icon: IconDefinition;
  label: string;
  route: SMLRouteParts;
  params?: Record<string, string>;
  /** The red count (Connections: the social accounts that need a new sign-in). */
  count?: number;
  /** The words a screen reader hears for the count. */
  countText?: string;
}

/**
 * design: navigation-v2 6a, 7d. The links of the account menu and the mobile account sheet:
 * Settings, Connections (red count), Plans and billing (the workspace owner only), Help and
 * support and "Review MUSIC24" (design review-form a11). The theme, the mobile app and Log out are not links, so the menu draws them itself.
 */
export function useAccountMenuLinks(accountAttention: number): AccountMenuLink[] {
  const intl = useIntl();
  // Only the workspace owner manages billing. While the role loads it reads as owner.
  const isOwner = useMyTeamRole() === 'OWNER';

  return useMemo(() => {
    const links: AccountMenuLink[] = [
      {
        key: 'settings',
        icon: faGear,
        label: intl.formatMessage(smlNavigationMessages.settings),
        ...settingsTabTarget(SMLSettingsTabId.account),
      },
      {
        key: 'connections',
        icon: faTowerBroadcast,
        label: intl.formatMessage(smlNavigationMessages.connections),
        ...settingsTabTarget(SMLSettingsTabId.connections),
        count: accountAttention > 0 ? accountAttention : undefined,
        countText:
          accountAttention > 0 ? intl.formatMessage(chrome.channelsReconnect, { count: accountAttention }) : undefined,
      },
    ];
    if (isOwner) {
      links.push({
        key: 'plans',
        icon: faCreditCard,
        label: intl.formatMessage(messages.plansAndBilling),
        ...settingsTabTarget(SMLSettingsTabId.subscription),
      });
    }
    links.push({
      key: 'help',
      icon: faLifeRing,
      label: intl.formatMessage(chrome.help),
      ...settingsTabTarget(SMLSettingsTabId.support),
    });
    links.push({
      key: 'review',
      icon: faStar,
      label: intl.formatMessage(reviewMessages.menuItem),
      route: SMLRoutes.review,
    });
    return links;
  }, [intl, isOwner, accountAttention]);
}
